import { externalApi } from '../../../shared/config/axiosConfig';
import { logDebug } from '../../../shared/config/globalFunctions';
import {
  fetchCurrentBrowserUser,
  fetchCurrentUserAvatar,
  refreshBrowserAccessToken,
} from '../../../services/webAPI/auth.service';
import { isInvalidOrRevokedCredentialError } from '../../../services/webAPI/authError';
import { publishAuthEvent } from '../../../services/webAPI/authChannel';
import {
  AUTH_STATUSES,
  AUTH_STATUS_ANONYMOUS,
  AUTH_STATUS_AUTHENTICATED,
  AUTH_STATUS_REFRESHING,
  AUTH_STATUS_REAUTHORIZATION_REQUIRED,
  LOGGED_IN,
  SET_ACCESS_TOKEN,
  SET_ACCESS_TOKEN_EXPIRY,
  SET_REAUTHORIZATION_REQUIRED,
  SET_AUTH_STATUS,
  SET_GUEST_TOKEN,
  SET_GUEST_MINT_STATUS,
  GUEST_MINT_STATUS_FAILED,
  GUEST_MINT_STATUS_PENDING,
  GUEST_MINT_STATUS_READY,
  GUEST_MINT_STATUSES,
  SAVE_USER_AVATAR,
} from '../../types/user';

/**
 * @typedef {object} LoggedInPayload
 * @property {import('global').UserDataResponse} user authenticated user data
 * @property {number} userId authenticated user identifier
 * @property {string} accessToken user access token
 * @property {string} role authenticated user role
 */

/**
 * Creates the canonical payload for every LOGGED_IN action.
 * @param {LoggedInPayload} payload login payload fields
 * @returns {{type: string, payload: LoggedInPayload}}
 */
export const loggedIn = ({ user, userId, accessToken, role }) => ({
  type: LOGGED_IN,
  payload: { user, userId, accessToken, role },
});

/**
 * Updates browser authentication lifecycle state.
 * @param {string} status authentication lifecycle status
 * @returns {{type: string, payload: string}}
 */
export const setAuthStatus = (status) => ({
  type: SET_AUTH_STATUS,
  payload: AUTH_STATUSES.includes(status) ? status : undefined,
});

/**
 * Replaces the disposable browser access token in Redux memory.
 * @param {string} accessToken current browser access token
 * @returns {{type: string, payload: string}}
 */
export const setAccessToken = (accessToken) => ({
  type: SET_ACCESS_TOKEN,
  payload: accessToken,
});

/**
 * Stores access-token expiry deadline in Redux memory.
 * @param {number} expiresAt expiry timestamp in milliseconds
 * @returns {{type: string, payload: number}}
 */
export const setAccessTokenExpiry = (expiresAt) => ({
  type: SET_ACCESS_TOKEN_EXPIRY,
  payload: expiresAt,
});

/**
 * Marks the browser session as requiring interactive authorization.
 * @returns {{type: string}}
 */
export const markReauthorizationRequired = () => ({
  type: SET_REAUTHORIZATION_REQUIRED,
});

/**
 * Records the in-memory guest mint outcome.
 * @param {'idle' | 'pending' | 'ready' | 'failed'} status guest mint status
 * @returns {{type: string, payload: string | undefined}}
 */
export const setGuestMintStatus = (status) => ({
  type: SET_GUEST_MINT_STATUS,
  payload: GUEST_MINT_STATUSES.includes(status) ? status : undefined,
});

const revokeAvatarObjectUrl = (avatar) => {
  if (typeof avatar === 'string' && avatar.length > 0) {
    URL.revokeObjectURL(avatar);
  }
};

let browserRefreshPromise = null;

/**
 * Refreshes the browser access token through the HttpOnly refresh session.
 * Concurrent callers in this tab share one request.
 * @param {{silent?: boolean}} [options] lifecycle behavior for the refresh
 * @returns {import('redux-thunk').ThunkAction<
 *   Promise<import('../../../services/webAPI/auth.service').BrowserAccessToken>,
 *   import('global').ReduxAppState,
 *   unknown,
 *   import('redux').UnknownAction
 * >}
 */
export const refreshAccessToken =
  ({ silent = false } = {}) =>
  (dispatch, getState) => {
    // One promise serves all callers in this tab; capture the epoch before awaiting.
    if (!browserRefreshPromise) {
      // Capture one immutable-in-time view before starting the async request.
      // Later completion must never apply to a newer auth session.
      const refreshStartState = getState();
      const wasAuthenticated = hasLiveAuth(refreshStartState);
      let refreshSessionEpoch = refreshStartState?.auth?.authSessionEpoch ?? 0;
      const isRefreshStillValid = () => {
        const authState = getState()?.auth;
        return (
          (authState?.authSessionEpoch ?? 0) === refreshSessionEpoch &&
          authState?.status !== AUTH_STATUS_ANONYMOUS &&
          authState?.status !== AUTH_STATUS_REAUTHORIZATION_REQUIRED
        );
      };
      // Bootstrap refresh shows pending state; proactive/interceptor refresh stays mounted.
      if (!silent) dispatch(setAuthStatus(AUTH_STATUS_REFRESHING));
      browserRefreshPromise = refreshBrowserAccessToken()
        .then(async (tokenResult) => {
          // A completion from a replaced session must never install its token.
          if (!isRefreshStillValid()) {
            const invalidatedError = /** @type {Error & {code?: string}} */ (
              new Error('Browser refresh invalidated by auth session change')
            );
            invalidatedError.code = 'AUTH_REFRESH_INVALIDATED';
            throw invalidatedError;
          }
          dispatch(setAccessToken(tokenResult.accessToken));
          const expiresAt =
            typeof tokenResult.expiresIn === 'number' &&
            Number.isFinite(tokenResult.expiresIn)
              ? Date.now() + tokenResult.expiresIn * 1000
              : 0;
          dispatch(setAccessTokenExpiry(expiresAt));
          publishAuthEvent({
            type: 'access-token-refreshed',
            accessToken: tokenResult.accessToken,
            expiresAt,
            authSessionEpoch: refreshSessionEpoch,
          });
          let userId = getState()?.user?.userId ?? 0;
          if (userId <= 0) {
            const user = await fetchCurrentBrowserUser(tokenResult.accessToken);
            if (!isRefreshStillValid()) {
              const invalidatedError = /** @type {Error & {code?: string}} */ (
                new Error('Browser refresh invalidated by auth session change')
              );
              invalidatedError.code = 'AUTH_REFRESH_INVALIDATED';
              throw invalidatedError;
            }
            dispatch(
              loggedIn({
                user,
                userId: user.Id,
                accessToken: tokenResult.accessToken,
                role: tokenResult.role ?? '',
              })
            );
            refreshSessionEpoch = getState()?.auth?.authSessionEpoch;
            userId = user.Id;
          }
          if (userId > 0) {
            try {
              const avatarBlob = await fetchCurrentUserAvatar(
                tokenResult.accessToken
              );
              if (isRefreshStillValid()) {
                const objectUrl = URL.createObjectURL(avatarBlob);
                if (isRefreshStillValid()) {
                  const previousAvatar = getState()?.user?.avatar;
                  revokeAvatarObjectUrl(previousAvatar);
                  dispatch({
                    type: SAVE_USER_AVATAR,
                    payload: objectUrl,
                  });
                } else {
                  revokeAvatarObjectUrl(objectUrl);
                }
              }
            } catch {
              // Missing avatars must not prevent authenticated login.
            }
            if (!isRefreshStillValid()) {
              const invalidatedError = /** @type {Error & {code?: string}} */ (
                new Error('Browser refresh invalidated by auth session change')
              );
              invalidatedError.code = 'AUTH_REFRESH_INVALIDATED';
              throw invalidatedError;
            }
          }
          return tokenResult;
        })
        .catch((error) => {
          if (!isRefreshStillValid()) {
            throw error;
          }
          if (isInvalidOrRevokedCredentialError(error)) {
            revokeAvatarObjectUrl(getState()?.user?.avatar);
            dispatch(markReauthorizationRequired());
            publishAuthEvent({
              type: 'reauthorization-required',
              authSessionEpoch: refreshSessionEpoch + 1,
            });
          } else if (wasAuthenticated) {
            // Network/5xx failures are transient, so preserve the authenticated session.
            dispatch(setAuthStatus(AUTH_STATUS_AUTHENTICATED));
          }
          throw error;
        })
        .finally(() => {
          browserRefreshPromise = null;
        });
    }
    return browserRefreshPromise;
  };

let guestMintPromise = null;

/**
 * Returns whether a live Redux state has interactive authentication.
 * @param {import('global').ReduxAppState} state current Redux state
 * @returns {boolean}
 */
export const hasLiveAuth = (state) => {
  const authState = state?.auth;
  return (
    authState?.status === AUTH_STATUS_AUTHENTICATED ||
    Boolean(authState?.authToken)
  );
};

/**
 * Mints a guest token for anonymous browsing. The token remains in Redux
 * memory only and is never included in the persisted store projection.
 * Single-flight: concurrent callers share one in-flight mint request.
 * @returns {import('redux-thunk').ThunkAction<
 *   Promise<string|null>,
 *   import('global').ReduxAppState,
 *   unknown,
 *   import('redux').UnknownAction
 * >}
 */
export const getGuestToken = () => (dispatch, getState) => {
  if (!guestMintPromise) {
    guestMintPromise = mintGuestToken(dispatch, getState);
  }
  return guestMintPromise;
};

/**
 * Performs the guest token mint request and stores the token in Redux.
 * Sets guestMintStatus to pending before the request, ready when a token is
 * stored, and failed when the result is null or the request throws.
 * Resets the single-flight guard when done so a later 401 can mint fresh.
 * @param {import('redux').Dispatch} dispatch the Redux dispatch function
 * @param {() => import('global').ReduxAppState} getState the Redux state getter
 * @returns {Promise<string|null>}
 */
const mintGuestToken = async (dispatch, getState) => {
  dispatch(setGuestMintStatus(GUEST_MINT_STATUS_PENDING));
  try {
    /** @type {import('axios').AxiosRequestConfig & {_guestMint: boolean}} */
    const requestConfig = {
      method: 'POST',
      url: '/auth/GetGuestToken',
      // Mark the mint so the 401-retry interceptor never retries it.
      _guestMint: true,
    };
    const response = await externalApi(requestConfig);
    // The JWT arrives in ApiResponse.Message, not Result: the controller calls
    // the positional ctor `new ApiResponse(HttpStatusCode.OK, "", token)` and
    // the ctor signature is ApiResponse(statusCode, result, message, token) -
    // so the token binds to `message` (result stays "").
    const token = response?.data?.Message;
    if (token && !hasLiveAuth(getState())) {
      dispatch({
        type: SET_GUEST_TOKEN,
        payload: token,
      });
      dispatch(setGuestMintStatus(GUEST_MINT_STATUS_READY));
    } else if (!token) {
      dispatch(setGuestMintStatus(GUEST_MINT_STATUS_FAILED));
    }
    // A token that loses the race to live auth is dropped. Leave the mint
    // pending rather than failed: failed with no access token opens the
    // public credential gate as error while sign-in is still settling.
    return token ?? null;
  } catch (error) {
    logDebug('getGuestToken failed', error);
    dispatch(setGuestMintStatus(GUEST_MINT_STATUS_FAILED));
    return null;
  } finally {
    guestMintPromise = null;
  }
};
